import { ChevronLeft, ChevronRight } from 'components/icons';
import { Button } from 'components/ui/button';
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem } from 'components/ui/pagination';
import useAuth from 'hooks/useAuth.ts';
import { GridLoader } from 'pages/dashboard/CasesTab';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'store';
import { useGetPaginatedTimecardsWithFiltersQuery } from 'store/api/timecards';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import TimecardList from '../TimecardList';
import { paginationItems } from './paginationItems';

interface UserDraftTimecardsListProps {
    setNumberOfDrafts?: (numberOfDrafts: number) => void;
    pageSize?: number;
    borderless?: boolean;
}

const UserDraftTimecardsList = ({ setNumberOfDrafts, pageSize = 5, borderless }: UserDraftTimecardsListProps) => {
    const { user } = useAuth();
    const userId = user?.id;

    const { draftTimecardsLoading } = useSelector((state) => state.timecard);

    const [currentPage, setCurrentPage] = useState(1);
    const { data, isFetching } = useGetPaginatedTimecardsWithFiltersQuery(
        {
            pageSize,
            page: currentPage - 1,
            sort: ['timecardDate,desc', 'stopwatchRunningSince,desc'],
            filters: `createdByEmployee.id==(${userId});timecardStatusTypeCode=in=(DRAFT)`,
        },
        { skip: !userId }
    );

    // TODO: effect event handler — `setNumberOfDrafts` mirrors query result to a parent callback.
    // Refactor by lifting the timecards query to the parent so it owns `totalElements` directly,
    // or pass an onCountChange callback invoked from within the query's success path.
    useEffect(() => {
        if (setNumberOfDrafts) {
            setNumberOfDrafts(data?.totalElements || 0);
        }
    }, [data?.totalElements, setNumberOfDrafts]);

    if (data && currentPage > 1 && data.content.length === 0) {
        setCurrentPage(data.totalPages > 0 ? data.totalPages : currentPage - 1);
    }

    const handlePageChange = useCallback((index: number) => {
        setCurrentPage(index);
    }, []);

    return (
        <div>
            {isFetching || draftTimecardsLoading ? (
                <GridLoader height={120} numberOfRows={5} />
            ) : (
                <TimecardList timecards={data?.content || []} compact borderless={borderless} />
            )}
            {data && data.totalPages > 1 ? (
                <Pagination className="justify-end px-4 py-5" aria-label={t('pagination.pagination')}>
                    <PaginationContent>
                        <PaginationItem>
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                aria-label={t('pagination.previous-page')}
                                disabled={currentPage <= 1}
                                onClick={() => handlePageChange(currentPage - 1)}
                            >
                                <ChevronLeft className="size-5" />
                            </Button>
                        </PaginationItem>
                        {paginationItems(data.totalPages, currentPage).map((item) => (
                            <PaginationItem key={item}>
                                {typeof item === 'number' ? (
                                    <Button
                                        variant="ghost"
                                        size="icon-sm"
                                        aria-current={item === currentPage ? 'page' : undefined}
                                        className={cn(
                                            'text-body2 font-normal',
                                            item === currentPage && 'bg-grey-900/8 hover:bg-grey-900/8'
                                        )}
                                        onClick={() => handlePageChange(item)}
                                    >
                                        {item}
                                    </Button>
                                ) : (
                                    <PaginationEllipsis className="size-8" />
                                )}
                            </PaginationItem>
                        ))}
                        <PaginationItem>
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                aria-label={t('pagination.next-page')}
                                disabled={currentPage >= data.totalPages}
                                onClick={() => handlePageChange(currentPage + 1)}
                            >
                                <ChevronRight className="size-5" />
                            </Button>
                        </PaginationItem>
                    </PaginationContent>
                </Pagination>
            ) : null}
        </div>
    );
};

export default UserDraftTimecardsList;
